/**
 * Sheet chrome headline above the fund tabs.
 *
 * Amounts: callers pass sheet-level buyAmount + sellAmount (already rounded like fund cards).
 * - Default "Switch your ₹X" → entry side (buy-entry → buyAmount, sell-entry → sellAmount).
 * - Copy that says Sell → sellAmount; copy that says Buy → buyAmount.
 * Does not belong here: tab/redeem amounts (steps + tax resolve sell ₹ at the body).
 */
import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import type { ReactNode } from 'react'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { PlansPlanMode } from '../../../api/types'
import {
  ALLOCATION_PLANS_THEME,
  REBALANCE_PLANS_THEME
} from '../../../plans-list/header/common/riskProfileHeaderTheme'
import { resolveSellLedPlanSwitchHeadlineSuffix } from '../../open/planRecommendationOpen'

export type PlanRecommendationIntroPlanMode =
  | PlansPlanMode.ALLOCATION
  | PlansPlanMode.REBALANCE

export const PLAN_RECOMMENDATION_INTRO_BY_PLAN_MODE = {
  [PlansPlanMode.ALLOCATION]: {
    label: 'Allocation Recommendation',
    color: ALLOCATION_PLANS_THEME.headerTextColor
  },
  [PlansPlanMode.REBALANCE]: {
    label: 'Rebalance Recommendation',
    color: REBALANCE_PLANS_THEME.headerTextColor
  }
} as const satisfies Record<
  PlanRecommendationIntroPlanMode,
  { label: string; color: string }
>

type PlanRecommendationIntroProps = {
  planMode: PlanRecommendationIntroPlanMode
  /** Sheet buy-side total (rounded). Buy-entry: root totalTransferAmount; sell-entry: sum of counter buyAmounts. */
  buyAmount: number
  /** Sheet sell-side total (rounded). Sell-entry: root totalTransferAmount; buy-entry: sum of counter sellAmounts. */
  sellAmount: number
  areBuyActionsCompleted: boolean
  areSellActionsCompleted: boolean
  entryAction: LumpsumRecommendationActionType.BUY | LumpsumRecommendationActionType.SELL
  counterFundCount: number
}

export function PlanRecommendationIntro({
  planMode,
  buyAmount,
  sellAmount,
  areBuyActionsCompleted,
  areSellActionsCompleted,
  entryAction,
  counterFundCount
}: PlanRecommendationIntroProps) {
  const { label, color: labelColor } = PLAN_RECOMMENDATION_INTRO_BY_PLAN_MODE[planMode]
  // Default switch line follows the fund that opened the sheet (root / totalTransferAmount side).
  const defaultSwitchAmount =
    entryAction === LumpsumRecommendationActionType.BUY ? buyAmount : sellAmount
  const defaultSwitchAmountLabel = formatRupeeLabel({ rawAmount: defaultSwitchAmount })
  const buyAmountLabel = formatRupeeLabel({ rawAmount: buyAmount })
  const sellAmountLabel = formatRupeeLabel({ rawAmount: sellAmount })
  const safeCounterFundCount = Math.max(counterFundCount, 0)
  const sellSourcesCount =
    entryAction === LumpsumRecommendationActionType.BUY ? safeCounterFundCount : 1
  const buyDestinationsCount =
    entryAction === LumpsumRecommendationActionType.SELL ? safeCounterFundCount : 1
  const defaultHeadlineSuffix =
    entryAction === LumpsumRecommendationActionType.SELL
      ? resolveSellLedPlanSwitchHeadlineSuffix(safeCounterFundCount)
      : ` to the new ${formatFundNoun(buyDestinationsCount)}`

  return (
    <div className="px-5">
      <div className="flex flex-col gap-2">
        <div
          className="text-[14px] font-[500] leading-[130%]"
          style={{ color: labelColor }}
        >
          {label}
        </div>
        <PlanRecommendationIntroHeadline
          areBuyActionsCompleted={areBuyActionsCompleted}
          areSellActionsCompleted={areSellActionsCompleted}
          buyAmountLabel={buyAmountLabel}
          sellAmountLabel={sellAmountLabel}
          sellSourcesCount={sellSourcesCount}
          buyDestinationsCount={buyDestinationsCount}
          defaultHeadline={(
            <div className="max-w-[85%] text-[20px] font-[400] leading-[130%] text-[#6A6E6E]">
              {'Switch your '}
              <span className="font-[500] text-[#232323]">{defaultSwitchAmountLabel} Lump Sum</span>
              {defaultHeadlineSuffix}
            </div>
          )}
        />
      </div>
    </div>
  )
}

export function PlanRecommendationIntroHeadline({
  areBuyActionsCompleted,
  areSellActionsCompleted,
  buyAmountLabel,
  sellAmountLabel,
  sellSourcesCount,
  buyDestinationsCount,
  defaultHeadline
}: {
  areBuyActionsCompleted: boolean
  areSellActionsCompleted: boolean
  buyAmountLabel: string
  sellAmountLabel: string
  sellSourcesCount: number
  buyDestinationsCount: number
  defaultHeadline: ReactNode
}) {
  if (areBuyActionsCompleted && areSellActionsCompleted) {
    return (
      <div className="max-w-[85%] text-[20px] font-[500] leading-[130%] text-[#232323]">
        All action completed
      </div>
    )
  }

  // Sentence says Sell → sellAmount (full recommended sell side, not remaining).
  if (areBuyActionsCompleted) {
    return (
      <div className="max-w-[85%] text-[20px] font-[400] leading-[130%] text-[#6A6E6E]">
        {'Buy action completed. Sell '}
        <span className="font-[500] text-[#232323]">{sellAmountLabel} Lump Sum</span>
        {` from your current ${formatFundNoun(sellSourcesCount)}`}
      </div>
    )
  }

  // Sentence says Buy → buyAmount.
  if (areSellActionsCompleted) {
    return (
      <div className="max-w-[85%] text-[20px] font-[400] leading-[130%] text-[#6A6E6E]">
        {'Sell action completed. Buy '}
        <span className="font-[500] text-[#232323]">{buyAmountLabel} Lump Sum</span>
        {` in recommended ${formatFundNoun(buyDestinationsCount)}`}
      </div>
    )
  }

  return <>{defaultHeadline}</>
}

export function formatFundNoun(fundCount: number): string {
  if (fundCount <= 1) {
    return 'fund'
  }
  return 'funds'
}

export function areCounterFundsActionsCompleted(
  counterFunds: { executed?: boolean | null }[] | null | undefined
): boolean {
  const funds = counterFunds ?? []
  return funds.length > 0 && funds.every((fund) => fund?.executed === true)
}
